import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';

interface Position {
  x: number;
  y: number;
}

interface SnakeGameProps {
  cardWidth?: number;
  cardHeight?: number;
}

const SnakeGame: React.FC<SnakeGameProps> = ({ cardWidth = 320, cardHeight = 200 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameLoopRef = useRef<number>();
  
  // Calculate grid size based on card dimensions
  const gridSize = 10;
  const gameWidth = Math.floor(cardWidth / gridSize) * gridSize;
  const gameHeight = Math.floor(cardHeight / gridSize) * gridSize;
  const cols = gameWidth / gridSize;
  const rows = gameHeight / gridSize;

  const [snake, setSnake] = useState<Position[]>([{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }]);
  const [food, setFood] = useState<Position>({ x: 5, y: 5 });
  const [direction, setDirection] = useState<Position>({ x: 1, y: 0 });
  const [gameRunning, setGameRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);

  // Generate random food position
  const generateFood = useCallback(() => {
    return {
      x: Math.floor(Math.random() * cols),
      y: Math.floor(Math.random() * rows)
    };
  }, [cols, rows]);

  // Initialize game
  const initGame = useCallback(() => {
    const initialSnake = [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }];
    setSnake(initialSnake);
    setFood(generateFood());
    setDirection({ x: 1, y: 0 });
    setGameRunning(false);
    setGameOver(false);
    setScore(0);
  }, [cols, rows, generateFood]);

  // Start game
  const startGame = () => {
    if (!gameRunning && !gameOver) {
      setGameRunning(true);
    }
  };

  // Restart game
  const restartGame = () => {
    initGame();
    setTimeout(() => setGameRunning(true), 100);
  };

  // Handle keyboard input
  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (!gameRunning) return;

    const { key } = event;
    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        setDirection(prev => prev.y === 0 ? { x: 0, y: -1 } : prev);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setDirection(prev => prev.y === 0 ? { x: 0, y: 1 } : prev);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        setDirection(prev => prev.x === 0 ? { x: -1, y: 0 } : prev);
        break;
      case 'ArrowRight':
        event.preventDefault();
        setDirection(prev => prev.x === 0 ? { x: 1, y: 0 } : prev);
        break;
    }
  }, [gameRunning]);

  // Game loop
  const gameLoop = useCallback(() => {
    if (!gameRunning) return;

    setSnake(currentSnake => {
      const newSnake = [...currentSnake];
      const head = { ...newSnake[0] };
      
      // Move head
      head.x += direction.x;
      head.y += direction.y;

      // Check wall collision
      if (head.x < 0 || head.x >= cols || head.y < 0 || head.y >= rows) {
        setGameOver(true);
        setGameRunning(false);
        return currentSnake;
      }

      // Check self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        setGameRunning(false);
        return currentSnake;
      }

      newSnake.unshift(head);

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => {
          const newScore = prev + 10;
          if (newScore > highScore) {
            setHighScore(newScore);
          }
          return newScore;
        });
        setFood(generateFood());
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [gameRunning, direction, food, cols, rows, generateFood, highScore]);

  // Draw game
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, gameWidth, gameHeight);

    // Draw snake
    ctx.fillStyle = '#0f0';
    snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? '#0f0' : '#090';
      ctx.fillRect(segment.x * gridSize, segment.y * gridSize, gridSize - 1, gridSize - 1);
    });

    // Draw food
    ctx.fillStyle = '#f00';
    ctx.fillRect(food.x * gridSize, food.y * gridSize, gridSize - 1, gridSize - 1);
  }, [snake, food, gameWidth, gameHeight, gridSize]);

  // Set up game loop
  useEffect(() => {
    if (gameRunning) {
      gameLoopRef.current = window.setInterval(() => {
        gameLoop();
      }, 150);
    } else {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameRunning, gameLoop]);

  // Set up keyboard listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // Initialize game on mount
  useEffect(() => {
    initGame();
  }, [initGame]);

  // Draw game
  useEffect(() => {
    draw();
  }, [draw]);

  // Load high score from localStorage
  useEffect(() => {
    const savedHighScore = localStorage.getItem('snakeHighScore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  // Save high score to localStorage
  useEffect(() => {
    localStorage.setItem('snakeHighScore', highScore.toString());
  }, [highScore]);

  return (
    <div 
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '8px',
        padding: '10px',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Game Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '8px',
        fontSize: '12px',
        color: '#fff'
      }}>
        <span>Score: {score}</span>
        <span>High: {highScore}</span>
      </div>

      {/* Game Canvas */}
      <canvas
        ref={canvasRef}
        width={gameWidth}
        height={gameHeight}
        style={{
          border: '2px solid #333',
          backgroundColor: '#000',
          borderRadius: '4px'
        }}
      />

      {/* Game Controls */}
      {!gameRunning && !gameOver && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#fff',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '20px',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)'
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Snake Game</h3>
          <p style={{ margin: '0 0 15px 0', fontSize: '12px' }}>Use arrow keys to move</p>
          <motion.button
            onClick={startGame}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: '#0f0',
              color: '#000',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            Start Game
          </motion.button>
        </motion.div>
      )}

      {/* Game Over */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#fff',
            background: 'rgba(0, 0, 0, 0.9)',
            padding: '20px',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)'
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#f00' }}>Game Over!</h3>
          <p style={{ margin: '0 0 5px 0', fontSize: '12px' }}>Final Score: {score}</p>
          {score === highScore && score > 0 && (
            <p style={{ margin: '0 0 15px 0', fontSize: '12px', color: '#0f0' }}>New High Score!</p>
          )}
          <motion.button
            onClick={restartGame}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: '#0f0',
              color: '#000',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            Play Again
          </motion.button>
        </motion.div>
      )}

      {/* Instructions */}
      <div style={{
        fontSize: '10px',
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
        marginTop: '5px'
      }}>
        {gameRunning ? 'Use arrow keys to move' : 'Click to start playing!'}
      </div>
    </div>
  );
};

export default SnakeGame; 